import { dayJs } from './dayjs'
import { include, isEqual, length, ternary } from './javascript'

const b64toFile = (b64Data, type, contentType = '', sliceSize = 512) => {
  const byteCharacters = atob(b64Data)
  const byteArrays = []
  const viewType = type ? '.' + type.split('/')?.[1] : '.png'
  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize)

    const byteNumbers = new Array(slice.length)
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i)
    }

    const byteArray = new Uint8Array(byteNumbers)
    byteArrays.push(byteArray)
  }
  // file.url && (file?.url.split(";")?.[0].split("/")?.[1] || "jpg")
  const blob = new Blob(byteArrays, { type: contentType })
  const file = new File([blob], `${Date.now() + viewType}`, {
    type: type ? type : 'image/png',
  })
  return file
}

const fieldContactFormat = contact => {
  let char
  if (contact) {
    let numbers = contact.toString().replace(/\D/g, '')
    if (numbers.length === 10) {
      char = { 0: '', 3: '-', 6: '-' }
    } else if (numbers.length < 11) {
      char = { 0: '', 2: '-', 6: '-' }
    } else if (include([11, 12], length(numbers))) {
      char = { 0: '', 3: '-', 7: '-' }
    } else if (isEqual(length(numbers), 13)) {
      char = { 0: '', 2: '-', 5: '-', 9: '-' }
    }
    contact = ''
    let newChar = 2
    for (let i = 0; i < numbers.length; i++) {
      if (char) {
        contact += (char[i] || '') + numbers[i]
      } else {
        contact += (i === newChar ? '-' : '') + numbers[i]
        if (i === newChar) {
          newChar = newChar + 4
        }
      }
    }
  }
  return contact
}

const validationTag = lang => {
  switch (lang) {
    case 'jp':
      return 'jp-validation'

    default:
      return 'en-validation'
  }
}

const getTranslationKeyById = (id, list = []) =>
  list?.find(elem => isEqual(id, elem?.id))?.key

const getIdByTranslationKey = (key, list = []) =>
  list?.find(elem => isEqual(key, elem?.key))?.id

const getIdByTranslationValue = (key, list = []) =>
  list?.find(elem => isEqual(key, elem?.value))?.label

const convertToSelectOptions = ({ t, list = [] }) => {
  return list?.map(item => ({
    label: t(item?.label),
    value: item?.value,
  }))
}

const modifyFileListKeys = list =>
  ternary(
    length(list),
    list?.map(file => ({
      name: file?.fileName,
      url: file?.fileUrl,
      uid: file?.dmsId,
      dmsId: file?.dmsId,
    })),
    [],
  )

const downloadReport = async (reportUrl, fileName, fileType) => {
  if (!reportUrl) {
    return
  }
  try {
    const response = await fetch(reportUrl)
    const blob = await response.blob()
    // Create an object URL for the Blob
    const blobUrl = URL.createObjectURL(blob)
    // Create a temporary link element to initiate download
    const link = document.createElement('a')
    link.href = blobUrl
    // link.setAttribute('download', filename); // Set the filename
    fileName && link.setAttribute('download', fileName || `file.${fileType}`)
    link.style.display = 'none'
    // Append the link to the document body
    document.body.appendChild(link)
    // Trigger a click event on the link to start the download
    link.click()
    // Clean up: remove the link and revoke the Blob URL after download starts
    document.body.removeChild(link)
    URL.revokeObjectURL(blobUrl)
  } catch (error) {
    // console.error('Error downloading the file:', error)
  }
}

const getReportFileName = reportUrl => {
  if (!reportUrl) return null
  try {
    const segment = new URL(reportUrl).pathname.split('/').pop()
    if (!segment) return null
    const base = decodeURIComponent(segment.split('?')[0])
    const lastDot = base.lastIndexOf('.')
    if (lastDot <= 0) return base
    return base.slice(0, lastDot)
  } catch {
    return null
  }
}

const maskNumber = ({ value }) => {
  if (!value) return ''
  const valueStr = value.toString()
  const maskedSection = valueStr.slice(0).replace(/./g, 'X')
  return maskedSection
}

export const get30Days = () => {
  const today = new Date()
  const thirtyDaysAgo = today.getTime() - 30 * 24 * 60 * 60 * 1000
  const fromDate = dayJs(thirtyDaysAgo).format('DD/MM/YYYY')
  const toDate = dayJs(today).format('DD/MM/YYYY')
  return { fromDate, toDate }
}

const getLocation = async () => {
  return new Promise((resolve, reject) => {
    if (navigator?.geolocation) {
      const showPosition = position => {
        const currentLocation = {
          latitude: position?.coords?.latitude,
          longitude: position?.coords?.longitude,
        }
        resolve(currentLocation)
      }
      const positionError = error => {
        // if (error.PERMISSION_DENIED) {
        //   console.log('Error: permission denied', error)
        //   return 'Geolocation is not enabled. Please enable to use this feature.'
        // } else {
        //   console.log('Other kind of error: ' + error)
        // }
        // reject(error)
      }
      navigator?.geolocation?.getCurrentPosition(showPosition, positionError)
    } else {
      reject()
      // eslint-disable-next-line no-console
      // console.log('Geolocation is not supported by this browser.')
    }
  })
}

const deepClone = value => {
  // Check for null or undefined
  if (value === null || typeof value !== 'object') {
    return value
  }

  // Handle Dates
  if (value instanceof Date) {
    return new Date(value.getTime())
  }

  // Handle Arrays
  if (Array.isArray(value)) {
    return value.map(item => deepClone(item))
  }

  // Handle Objects
  if (value instanceof Object) {
    const copy = {}
    for (const key in value) {
      if (value.hasOwnProperty(key)) {
        copy[key] = deepClone(value[key])
      }
    }
    return copy
  }

  // Handle other cases (like Map, Set, etc.)
  if (value instanceof Map) {
    return new Map(
      Array.from(value.entries()).map(([k, v]) => [deepClone(k), deepClone(v)]),
    )
  }

  if (value instanceof Set) {
    return new Set(Array.from(value).map(item => deepClone(item)))
  }

  // If none of the above, return the value as is
  return value
}

export const numberFormat = (value, decimals = true, format = true) => {
  const num = Number(value)
  const isDecimals = decimals && num % 1 !== 0

  if (isNaN(num)) return ''

  return format
    ? num.toLocaleString(
        'en-IN',
        isDecimals && {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        },
      )
    : num
}

export const getColumnTitleWithMandatory = (
  title,
  t,
  showMandatory = false,
) => {
  if (!showMandatory) {
    return t(title)
  }
  return (
    <div className="column-title-with-mandatory">
      <p>{t(title)}</p>
      <p className="mandatory-tag">{t('txt_MANDATORY')}</p>
    </div>
  )
}

export const fieldUnits = {
  KG: 'dash_Kgs',
  MT: 'dash_MT',
  number: 'unit_Number',
}

export const setFieldUnit = ({ t, fieldName, unit = 'KG' } = {}) => {
  return `${t(fieldName)} (${t(fieldUnits[unit])})`
}

const numberFormatting = (value, countryCode = 'IN') => {
  const num = formatToDecimals(value) || null
  switch (countryCode) {
    case 'IN':
      return new Intl.NumberFormat('en-IN').format(num)

    default:
      return num
  }
}

const convertFormattedNumbers = value => {
  if (!value) return null
  return parseFloat(value.replace(',', ''))
}

export const formatToDecimals = (value, decimals = 3) => {
  if (!value) return null
  const num = typeof value === 'number' ? value : Number(value)
  if (Number.isNaN(num)) return null
  if (!Number.isInteger(num)) {
    return Number(num.toFixed(decimals))
  }
  return num
}

const formatDecimalValue = (value, decimals = 2) => {
  if (value === null || value === undefined || value === '') {
    return 0
  }
  const numValue = typeof value === 'string' ? Number(value) : value
  if (isNaN(numValue)) {
    return 0
  }
  if (numValue % 1 !== 0) {
    return Number(numValue.toFixed(decimals))
  }
  return numValue
}

const getDaysDiff = (fromDateStr, toDateStr) => {
  // Parse DD/MM/YYYY safely
  const [fromDay, fromMonth, fromYear] = fromDateStr.split('/').map(Number)
  const [toDay, toMonth, toYear] = toDateStr.split('/').map(Number)

  const fromDate = new Date(fromYear, fromMonth - 1, fromDay)
  const toDate = new Date(toYear, toMonth - 1, toDay)

  const oneDayMs = 24 * 60 * 60 * 1000

  const diffInMs = toDate.getTime() - fromDate.getTime()
  const diffInDays = Math.floor(diffInMs / oneDayMs) + 1 // +1 for inclusive

  return diffInDays < 0 ? 0 : diffInDays
}

const calculateValueForDateRange = (
  totalValue,
  year,
  fromDateStr,
  toDateStr,
) => {
  if (!fromDateStr || !toDateStr) return 0
  const isLeapYear = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0
  const daysInYear = isLeapYear ? 366 : 365
  const perDayValue = formatDecimalValue(totalValue / daysInYear)
  const selectedDays = getDaysDiff(fromDateStr, toDateStr)
  if (selectedDays < 0) return 0

  return perDayValue * selectedDays
}

const imageTypeFromName = name => {
  if (!name || typeof name !== 'string') return ''
  const ext = name.split('.').pop()?.toLowerCase()
  const mime = {
    pdf: 'application/pdf',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    webp: 'image/webp',
  }
  return mime[ext] ?? ''
}

const formatMaterialRatio = value1 => {
  if (value1 == null || value1 === '') return null
  const numValue1 = typeof value1 === 'string' ? Number(value1) : value1
  if (Number.isNaN(numValue1)) return null
  const firstDigit = parseInt(
    Math.floor(Math.abs(numValue1)).toString()[0] || '0',
    10,
  )
  const value2 = 10 - firstDigit
  return `${firstDigit}:${value2}`
}

export {
  b64toFile,
  fieldContactFormat,
  validationTag,
  getTranslationKeyById,
  modifyFileListKeys,
  downloadReport,
  maskNumber,
  getLocation,
  deepClone,
  getIdByTranslationKey,
  getIdByTranslationValue,
  calculateValueForDateRange,
  formatDecimalValue,
  imageTypeFromName,
  formatMaterialRatio,
  getReportFileName,
  numberFormatting,
  convertFormattedNumbers,
  convertToSelectOptions,
}
